import merge from "lodash/merge";

import type { Message, MessageData } from "../Message";
import { MessageFilter } from "../MessageFilter";

import { type ReaderLike } from "./ReaderLike";

class Zipper<D extends MessageData> {
  private chunkGenerators: ReturnType<ReaderLike<D>["iterMessageChunks"]>[];
  private chunks: Message<D>[][];
  // current read position for each chunk in #chunks
  // starts at 0, and is incremented after each message is read
  private readPositions: number[];

  constructor(
    chunkGenerators: ReturnType<ReaderLike<D>["iterMessageChunks"]>[],
  ) {
    this.chunkGenerators = chunkGenerators;
    this.chunks = [];
    for (let i = 0; i < chunkGenerators.length; i++) {
      this.chunks.push([]);
    }
    this.readPositions = Array.from<number>({
      length: chunkGenerators.length,
    }).fill(0);
  }

  public async getNext() {
    const nextEarliestTimestamp = await this.getNextEarliestTimestamp();
    let combinedMessage: Message<D> = {
      logTime: nextEarliestTimestamp,
      publishTime: 0n,
      sequence: 0,
      data: {} as D,
    };

    for (let i = 0; i < this.chunks.length; i++) {
      const msg = this.getMessageFromReader(i);
      if (msg && msg.logTime === nextEarliestTimestamp) {
        if (this.chunks.length === 1) {
          // Don't copy potentially large objects if it's not needed.
          combinedMessage = msg;
        } else {
          // If a single message needs to be stitched together from multiple readers, then do
          // the stitching.
          merge(combinedMessage, msg);
        }
        await this.advanceReader(i);
      }
    }

    return combinedMessage;
  }

  public async initialize() {
    for (let i = 0; i < this.chunkGenerators.length; i++) {
      await this.loadNextChunkIfNeeded(i);
    }
  }

  public hasMore() {
    return this.chunks.some((_, i) => this.chunkHasMoreMessages(i));
  }

  private async advanceReader(readerIndex: number) {
    this.assertReaderIndexInBounds(readerIndex);

    this.readPositions[readerIndex]++;
    await this.loadNextChunkIfNeeded(readerIndex);
  }

  private assertReaderIndexInBounds(readerIndex: number) {
    if (readerIndex >= this.chunkGenerators.length) {
      throw new Error(
        "Error encountered reading topic data. Please reach out to Roboto support.",
      );
    }
  }

  private chunkHasMoreMessages(readerIndex: number) {
    return this.readPositions[readerIndex] < this.chunks[readerIndex].length;
  }

  private getMessageFromReader(readerIndex: number) {
    this.assertReaderIndexInBounds(readerIndex);

    const chunk = this.chunks[readerIndex];
    const position = this.readPositions[readerIndex];
    return chunk[position];
  }

  private async getNextEarliestTimestamp() {
    let nextEarliestTimestamp = 2n ** 64n - 1n;
    for (let i = 0; i < this.chunks.length; i++) {
      if (!this.chunkHasMoreMessages(i)) {
        await this.loadNextChunkIfNeeded(i);
      }
      const msg = this.getMessageFromReader(i);
      if (msg) {
        nextEarliestTimestamp =
          msg.logTime < nextEarliestTimestamp
            ? msg.logTime
            : nextEarliestTimestamp;
      }
    }
    return nextEarliestTimestamp;
  }

  private async loadNextChunkIfNeeded(readerIndex: number) {
    if (this.readPositions[readerIndex] >= this.chunks[readerIndex].length) {
      const nextChunk = await this.chunkGenerators[readerIndex].next();
      if (nextChunk.done) {
        this.chunks[readerIndex] = [];
      } else {
        this.chunks[readerIndex] = nextChunk.value;
        this.readPositions[readerIndex] = 0;
      }
    }
  }
}

export async function* zipMessages<T extends MessageData>(
  readers: ReaderLike<T>[],
  options?: { filter?: MessageFilter; signal?: AbortSignal },
): AsyncGenerator<Message<T>> {
  const generators = readers.map((reader) =>
    reader.iterMessageChunks({
      filter: options?.filter,
      abortSignal: options?.signal,
      validateCrcs: false,
    }),
  );

  const zipper = new Zipper<T>(generators);
  await zipper.initialize();

  while (true) {
    options?.signal?.throwIfAborted();

    if (!zipper.hasMore()) {
      return;
    }

    yield await zipper.getNext();
  }
}
